import * as THREE from "three";
import { VFX } from "../../../../../art/vfx.js";
import { groundAt } from "../terrain";
import { reducedVfx } from "../../ui/settings";
import { hqNode } from "./common";
import { TowerView } from "./towerView";

/**
 * The fortress between strikes (fortress v2, 2026-10-19: "the fortress is the epic element of the game"). Its baked
 * idle already moves every banner, lantern, bell, coil, crown and window; this adds the pooled-VFX life on top, the way
 * the lightning and fire towers get theirs (towerMotion `idle`):
 *
 *   embers   a puff of embers off a random torch or brazier (`flame*`) every ~0.22 s, now and then a wisp of soot
 *   crackle  the conductor talks to itself: an idle arc from the crystal tip (`condgem0`) to a coil (`coil*`), 2.5–4.5 s
 *   glints   a gold-white glint off a gem, the crystal or a window (`gem*`, `condgem*`, `win*`) every 0.6–1.2 s
 *
 * The torches and braziers themselves burn as sprite flames (towerMotion `braziers`, as on the fire tower). Off under
 * reduced VFX; the pool's own priorities drop ambient spawns first when it is busy (vfx.js).
 */
export const FortressLife = {
  EmberEvery: 0.22,
  SootChance: 0.2,
  SootLift: 0.18,
  ArcEvery: [2.5, 4.5] as const,
  GlintEvery: [0.6, 1.2] as const,
  GlintColour: [1, 0.9, 0.62] as [number, number, number],
};

interface Life { ember: number; arc: number; glint: number; flames: THREE.Object3D[]; coils: THREE.Object3D[]; tip: THREE.Object3D | null; shiny: THREE.Object3D[] }
const LIFE = new WeakMap<TowerView, Life>();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();

/** The model's numbered parts (`flame0`, `flame1` …) under either spelling of the node name (see hqNode). */
function parts(v: TowerView, part: string): THREE.Object3D[] {
  const re = new RegExp(`^${v.model.name}\\.?${part}\\d+$`);
  const out: THREE.Object3D[] = [];
  v.model.storeys.traverse((o) => { if (re.test(o.name)) out.push(o); });
  return out;
}

function lifeOf(v: TowerView): Life {
  let l = LIFE.get(v);
  if (!l) {
    const tips = parts(v, "condgem");
    l = { ember: Math.random() * FortressLife.EmberEvery, arc: FortressLife.ArcEvery[0] * Math.random(), glint: FortressLife.GlintEvery[0],
          flames: parts(v, "flame"), coils: parts(v, "coil"), tip: tips.find((o) => /condgem0$/.test(o.name)) ?? null,
          shiny: [...parts(v, "gem"), ...tips, ...parts(v, "win")] };
    LIFE.set(v, l);
  }
  return l;
}

function between(r: readonly [number, number]): number { return r[0] + Math.random() * (r[1] - r[0]); }

/** One frame of the fortress's idle life. Visible parts only: a part the clips have parked (scale ~0) is skipped. */
export function fortressIdle(v: TowerView, dt: number, fx: VFX): void {
  if (reducedVfx()) return;
  const l = lifeOf(v);
  const live = (o: THREE.Object3D) => o.scale.x > 0.05;
  if ((l.ember -= dt) <= 0) {
    l.ember = FortressLife.EmberEvery * (0.7 + Math.random() * 0.6);
    const f = l.flames.length ? l.flames[Math.floor(Math.random() * l.flames.length)] : null;
    if (f && live(f)) {
      fx.idleEmber(f.getWorldPosition(_a));
      if (Math.random() < FortressLife.SootChance) fx.idleSoot(_a.setY(_a.y + FortressLife.SootLift));
    }
  }
  if ((l.arc -= dt) <= 0) {
    l.arc = between(FortressLife.ArcEvery);
    if (l.tip && l.coils.length && live(l.tip)) fx.idleArc(l.tip.getWorldPosition(_a), l.coils[Math.floor(Math.random() * l.coils.length)].getWorldPosition(_b));
  }
  if ((l.glint -= dt) <= 0) {
    l.glint = between(FortressLife.GlintEvery);
    const s = l.shiny.length ? l.shiny[Math.floor(Math.random() * l.shiny.length)] : null;
    if (s && live(s)) fx.sparkles(s.getWorldPosition(_a), 2, { color: FortressLife.GlintColour, radius: 0.05, life: 0.5, size: 0.09 });
  }
}

/** The stock fortress's reveal beat (its `show` charges the conductor and flashes it at 2.0 s): a gold-white flash and
 * bloom on the crystal, a gold ring spreading over the keep and gold motes thrown off the tip. */
export const AWAKEN_GOLD: [number, number, number] = [1, 0.84, 0.45];
export function fortressAwaken(v: TowerView, fx: VFX): void {
  const tip = hqNode(v.model.storeys, v.model.name, "condgem0");
  const at = tip ? tip.getWorldPosition(_a) : _a.copy(v.root.position).setY(v.box.max.y);
  fx.flash(at, 1.2, [1, 0.97, 0.88]);
  fx.glowPulse(at, { size: 1.4, life: 0.6, color: AWAKEN_GOLD });
  v.box.getCenter(_b);
  _b.y = groundAt(_b.x, _b.z);
  fx.ring(_b, { size: 3.4, life: 0.8, color: AWAKEN_GOLD, flat: true, bright: 1.5 });
  fx.sparkles(at, 10, { color: AWAKEN_GOLD, radius: 0.45, rise: 0.5, life: 1.1, size: 0.1 });
}
